import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const authHeader = (): Record<string, string> => {
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Comentários do veículo (at-90, ADR 0042).
 *
 * O "feedback vivo" que faltava ao lado das sub-tarefas (o que foi feito) e das tags de esteira
 * (o motivo da parada): o texto livre que diz o que está acontecendo, com resposta de quem
 * responde por aquilo — "o associado veio e reclamou do desamassado" / "serviço refeito".
 *
 * ⚠️ NENHUMA função aqui tem catch: "não há comentário" e "não consegui perguntar" levam a
 * conclusões opostas, e a segunda vira afirmação errada na frente do associado. Quem trata o erro
 * é a tela, com faixa visível (regra da at-60).
 *
 * 🔴 Comentário é INTERNO e não se exclui: nada disto aparece no PDF do checklist nem na tela
 * pública do associado. Desde a at-140 (ADR 0068), o autor corrige o texto nos primeiros 10
 * minutos, se ninguém respondeu.
 */
export type ComentarioEscopo = 'PLACA' | 'ESTEIRA' | 'SUBTAREFA';

/** at-129: quem foi mencionado (@) num comentário, resolvido pelo servidor. */
export interface PessoaMencionada {
  id: string;
  username: string | null;
  fullName: string | null;
}

export interface RespostaComentario {
  id: string;
  texto: string;
  autorId: string | null;
  autorNome: string;
  criadoEm: string;
  /** Só o que o servidor confirmou é destacado no texto — `@qualquercoisa` solto fica como texto. */
  mencionados?: PessoaMencionada[];
  /** at-140: quando o autor corrigiu o texto; `null` = nunca. */
  editadoEm?: string | null;
  /** at-140: até quando o autor ainda edita. Quem decide é o servidor; a tela só esconde o botão. */
  editavelAte?: string;
}

export interface ThreadComentario extends RespostaComentario {
  escopo: ComentarioEscopo;
  /** Rótulo pronto do servidor: "Placa", o nome da esteira, ou "Sub-tarefa: <nome>". */
  escopoLabel: string;
  esteiraId: string | null;
  esteiraNome: string | null;
  atividadeSubtarefaId: string | null;
  subtarefaNome: string | null;
  /** Nasceu em OUTRO atendimento da mesma placa (escopo PLACA atravessa re-entradas). */
  deOutroAtendimento: boolean;
  respostas: RespostaComentario[];
}

export interface ComentariosDoVeiculo {
  carId: string;
  placa: string;
  /** Esteiras que o seletor oferece: as do planejamento e do histórico, mais a atual. */
  esteirasComentaveis: Array<{ id: string; nome: string }>;
  /**
   * Permissões EFETIVAS do requerente, vindas da matriz configurável. Chegam nesta resposta de
   * propósito: sem elas a seção não sabe se mostra o campo de escrita, e uma segunda requisição
   * faria a seção renderizar em dois tempos.
   */
  podeComentar: boolean;
  podeResponder: boolean;
  threads: ThreadComentario[];
}

export const obterComentariosDoVeiculo = async (carId: string): Promise<ComentariosDoVeiculo> => {
  const { data } = await axios.get(`${BACKEND_URL}/comentarios/cars/${carId}`, {
    headers: authHeader(),
  });
  return data;
};

export const criarComentario = async (
  carId: string,
  payload: {
    escopo: ComentarioEscopo;
    texto: string;
    esteiraId?: string;
    atividadeSubtarefaId?: string;
    /** at-129: ids escolhidos no autocomplete de @. O servidor descarta quem não está ativo. */
    mencionados?: string[];
  },
): Promise<ThreadComentario> => {
  const { data } = await axios.post(`${BACKEND_URL}/comentarios/cars/${carId}`, payload, {
    headers: authHeader(),
  });
  return data;
};

/** Thread plano: a resposta é sempre ao comentário ORIGINAL, nunca a outra resposta. */
export const responderComentario = async (
  comentarioId: string,
  texto: string,
  mencionados: string[] = [],
): Promise<RespostaComentario> => {
  const { data } = await axios.post(
    `${BACKEND_URL}/comentarios/${comentarioId}/respostas`,
    { texto, mencionados },
    { headers: authHeader() },
  );
  return data;
};

/** at-140: o autor corrige o texto (comentário ou resposta) nos primeiros 10 minutos. */
export const editarComentario = async (
  comentarioId: string,
  texto: string,
  mencionados: string[] = [],
): Promise<RespostaComentario> => {
  const { data } = await axios.patch(
    `${BACKEND_URL}/comentarios/${comentarioId}`,
    { texto, mencionados },
    { headers: authHeader() },
  );
  return data;
};

// ------------------------------------------------------------------ configuração (ADMIN/TI)

export type ComentarioAcao = 'COMENTAR' | 'RESPONDER';

export interface PermissaoComentario {
  acao: ComentarioAcao;
  papeis: string[];
}

export const listarPermissoesDeComentario = async (): Promise<PermissaoComentario[]> => {
  const { data } = await axios.get(`${BACKEND_URL}/comentarios/admin/permissoes`, {
    headers: authHeader(),
  });
  return data;
};

/** Lista vazia nega a todos, menos administradores — a matriz é deny-by-default. */
export const definirPermissaoDeComentario = async (
  acao: ComentarioAcao,
  papeis: string[],
): Promise<void> => {
  await axios.put(
    `${BACKEND_URL}/comentarios/admin/permissoes/${acao}`,
    { papeis },
    { headers: authHeader() },
  );
};
